import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { filterMedia, getHotMedia, getYearOptions } from "@/lib/media";
import type { MediaType } from "@/types/movie";
import { TYPE_LABELS } from "@/types/movie";

const TYPE_FILTERS: { value: MediaType | "all"; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "variety", label: "综艺" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState<MediaType | "all">("all");
  const [yearFilter, setYearFilter] = useState("all");

  useDocumentMeta(
    query ? `搜索“${query}” - 91在线观看视频` : "搜索影视作品 - 91在线观看视频",
    "在91在线观看视频搜索电影、电视剧和综艺节目，快速找到你想看的精彩内容。",
    "影视搜索,在线观看搜索,91在线观看视频",
  );

  const years = useMemo(() => getYearOptions(), []);
  const hotMedia = useMemo(() => getHotMedia(4), []);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    return filterMedia({ keyword: query, type: typeFilter, year: yearFilter });
  }, [query, typeFilter, yearFilter]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    setParams(next ? { q: next } : {});
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-10">
      <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">影视搜索</h1>
      <p className="mt-2 text-sm text-foreground-600">输入片名、导演、演员或类型关键词，快速定位你想看的内容。</p>

      <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <div className="flex h-12 flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4">
          <i className="ri-search-line text-lg text-foreground-500"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索电影、电视剧、综艺"
            className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
          />
        </div>
        <button
          type="submit"
          className="flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-7 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </form>

      {query.trim() && (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {TYPE_FILTERS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTypeFilter(opt.value)}
                className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  typeFilter === opt.value
                    ? "bg-primary-600 text-background-50"
                    : "border border-background-200 bg-background-100 text-foreground-700 hover:text-primary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
            <span className="mx-1 hidden h-5 w-px bg-background-300 sm:block"></span>
            <button
              type="button"
              onClick={() => setYearFilter("all")}
              className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${
                yearFilter === "all"
                  ? "bg-accent-500 text-primary-950"
                  : "border border-background-200 bg-background-100 text-foreground-700 hover:text-primary-600"
              }`}
            >
              全部年份
            </button>
            {years.slice(0, 5).map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setYearFilter(y)}
                className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  yearFilter === y
                    ? "bg-accent-500 text-primary-950"
                    : "border border-background-200 bg-background-100 text-foreground-700 hover:text-primary-600"
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          <p className="mt-5 text-sm text-foreground-600">
            关于“<span className="font-semibold text-foreground-900">{query}</span>”共找到{" "}
            <span className="font-semibold text-primary-600">{results.length}</span> 部相关作品
          </p>
        </>
      )}

      {query.trim() && results.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {results.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {query.trim() && results.length === 0 && (
        <div className="mt-6">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="未找到相关内容"
            description="换个关键词试试，或看看下面的热门推荐。"
          />
          <div className="mt-10">
            <SectionHeading title="热门影视推荐" subtitle="大家正在看的高人气作品" moreTo="/category/movie" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {hotMedia.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}

      {!query.trim() && (
        <div className="mt-8">
          <SectionHeading title="大家都在搜" subtitle="热门影视作品，点一点直接看" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {hotMedia.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {["科幻", "古装", "悬疑", "都市", "喜剧"].map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}